// File matching utility for batch upload
// Detects source vs benchmark files and pairs them by base name

export interface ParsedFile {
    file: File;
    baseName: string;
    type: 'source' | 'benchmark' | 'unknown';
    lang: string;
}

export interface FilePair {
    id: string;
    source: ParsedFile | null;
    benchmark: ParsedFile | null;
    status: 'paired' | 'source_only' | 'benchmark_only';
    /** Bundle credits saved at upload — written to file.estimatedCredits */
    estimatedCredits?: {
        translate: number;
        qc: number | null;
        semanticMatch: number | null;
        audit: number | null;
        pricingVersion: number | null;
        bundleType: 'translate' | 'train';
        computedAt: string;
    };
    /**
     * Parsed result từ modal (server stateless parse).
     * Khi user click "Tiến hành Nạp", parseOneFile dùng cache này → skip API call thứ 2.
     * Chỉ DOCX/PPTX có (PDF/TXT/MD path khác). Lifecycle: chỉ tồn tại khi truyền từ modal sang useFileActions.
     */
    parsedResult?: import('../services/api/docxApi').ParseStatelessResponse;
    /**
     * Benchmark text đã parse từ modal (train mode).
     * Khi commit, handleBatchImport reuse → skip parseFile lần 2 cho benchmark DOCX lớn.
     */
    benchmarkText?: string;
}

// Build pattern: "<CODE>.tên-file.ext" — prefix code + dấu chấm. ONLY rule.
// Vd code='vi' → match "VI.foo.docx" hoặc "vi.foo.docx" (case-insensitive)
function buildLangPattern(code: string): RegExp {
    const safe = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // escape regex
    return new RegExp(`^${safe}\\.`, 'i');
}

/** Readable rule cho UI. Trả về pattern duy nhất theo cặp lang project. */
export function getPairingRule(sourceCode: string, targetCode: string): string {
    const s = sourceCode.toUpperCase();
    const t = targetCode.toUpperCase();
    return `${s}.tên-file.ext  ⇄  ${t}.tên-file.ext`;
}


/**
 * Extract base name — bỏ extension + bỏ prefix lang code (vd "VI." / "EN.")
 */
function extractBaseName(fileName: string, sourcePat: RegExp, targetPat: RegExp): string {
    return fileName
        .replace(/\.[^.]+$/, '')   // bỏ extension cuối
        .replace(sourcePat, '')    // bỏ prefix source (vd "VI.")
        .replace(targetPat, '')    // bỏ prefix target (vd "EN.")
        .trim();
}


/**
 * Detect file type — chỉ check prefix `<CODE>.` (1 quy tắc duy nhất)
 */
function detectFileType(
    fileName: string,
    sourceCode: string, sourcePat: RegExp,
    targetCode: string, targetPat: RegExp,
): { type: 'source' | 'benchmark' | 'unknown'; lang: string } {
    if (sourcePat.test(fileName)) return { type: 'source', lang: sourceCode };
    if (targetPat.test(fileName)) return { type: 'benchmark', lang: targetCode };
    return { type: 'unknown', lang: '' };
}

/**
 * Parse a single file
 */
export function parseFile(file: File, sourceCode = 'vi', targetCode = 'en'): ParsedFile {
    const sp = buildLangPattern(sourceCode);
    const tp = buildLangPattern(targetCode);
    const { type, lang } = detectFileType(file.name, sourceCode, sp, targetCode, tp);
    const baseName = extractBaseName(file.name, sp, tp);
    return { file, baseName, type, lang };
}

/**
 * Match files into pairs
 */
export function matchFiles(files: File[], sourceCode = 'vi', targetCode = 'en'): FilePair[] {
    const parsed = files.map(f => parseFile(f, sourceCode, targetCode));

    // Group by base name
    const groups = new Map<string, { sources: ParsedFile[]; benchmarks: ParsedFile[]; unknowns: ParsedFile[] }>();

    for (const p of parsed) {
        if (!groups.has(p.baseName)) {
            groups.set(p.baseName, { sources: [], benchmarks: [], unknowns: [] });
        }
        const group = groups.get(p.baseName)!;

        if (p.type === 'source') {
            group.sources.push(p);
        } else if (p.type === 'benchmark') {
            group.benchmarks.push(p);
        } else {
            group.unknowns.push(p);
        }
    }

    // Build pairs
    const pairs: FilePair[] = [];

    groups.forEach((group, baseName) => {
        const source = group.sources[0] || group.unknowns[0] || null;
        const benchmark = group.benchmarks[0] || null;

        // If we used an unknown as source, remove it from unknowns
        const remainingUnknowns = group.unknowns.filter(u => u !== source);

        if (source || benchmark) {
            pairs.push({
                id: crypto.randomUUID(),
                source,
                benchmark,
                status: source && benchmark ? 'paired' : source ? 'source_only' : 'benchmark_only',
            });
        }

        // Handle remaining unknowns as source-only
        for (const unknown of remainingUnknowns) {
            pairs.push({
                id: crypto.randomUUID(),
                source: { ...unknown, type: 'source' },
                benchmark: null,
                status: 'source_only',
            });
        }

        // Handle extra sources
        for (let i = 1; i < group.sources.length; i++) {
            pairs.push({
                id: crypto.randomUUID(),
                source: group.sources[i],
                benchmark: null,
                status: 'source_only',
            });
        }

        // Handle extra benchmarks (orphans)
        for (let i = 1; i < group.benchmarks.length; i++) {
            pairs.push({
                id: crypto.randomUUID(),
                source: null,
                benchmark: group.benchmarks[i],
                status: 'benchmark_only',
            });
        }
    });

    return pairs.sort((a, b) => {
        const nameA = a.source?.file.name || a.benchmark?.file.name || '';
        const nameB = b.source?.file.name || b.benchmark?.file.name || '';
        return nameA.localeCompare(nameB);
    });
}
